/**
 * Session reset — clears per-user notification state when the signed-in
 * identity changes.
 *
 * Login/logout is a pure in-SPA transition (no page reload), and the
 * notification stores are global singletons. Without this, signing out of
 * tenant-1 and into tenant-2 left tenant-1's alerts, pipeline-run logs,
 * pending actions and approvals visible in the notification bar — the run-log
 * store is even persisted to localStorage, so it also survived reloads.
 *
 * We subscribe to the auth store and reset every notification store whenever
 * the user id changes AWAY from a signed-in identity (sign-out or user switch).
 * We deliberately do NOT reset on a null → user transition: that is the
 * page-load session restore, where the user's own persisted run logs should
 * survive. Importing this module for its side effect (see main.tsx) installs
 * the subscription once.
 */
import { useAuthStore } from './authStore';
import { useAlertStore } from './alertStore';
import { useRunLogStore } from './runLogStore';
import { useApprovalStore } from './approvalStore';
import { useHumanActionsStore } from './humanActionsStore';
import { useReleaseStore } from './releaseStore';
import { useWorkspaceStore } from '../modules/files/workspaceStore';
import { clearModuleState } from '../modules/files/moduleState';

let prevUserId: string | null = useAuthStore.getState().user?.id ?? null;

useAuthStore.subscribe((state) => {
  const nextUserId = state.user?.id ?? null;
  if (nextUserId === prevUserId) return;

  const wasSignedIn = prevUserId !== null;
  prevUserId = nextUserId;

  // null → user is the reload/restore path — keep the restored user's own
  // persisted run logs. Only tear down when leaving or replacing a session.
  if (!wasSignedIn) return;

  useAlertStore.getState().reset();
  useRunLogStore.getState().clearAll();
  useApprovalStore.getState().reset();
  useHumanActionsStore.getState().reset();
  useReleaseStore.getState().reset();
  useWorkspaceStore.getState().reset();
  clearModuleState();
});
